import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';

type Theme = 'light' | 'dark' | 'system';
type ThemeVariant = 'standard' | 'premium';

interface ThemeContextType {
  theme: Theme;
  themeVariant: ThemeVariant;
  setTheme: (theme: Theme) => void;
  toggleDarkMode: () => void;
  isPremium: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    // Try to get the theme from localStorage
    const savedTheme = localStorage.getItem('theme') as Theme;
    return savedTheme || 'system';
  });
  
  // Get auth context to determine if user has premium access
  const auth = useAuth();
  const isPremium = auth?.hasPermission('manage_settings') || false;
  const themeVariant: ThemeVariant = isPremium ? 'premium' : 'standard';

  // Update the theme attribute on the document element
  useEffect(() => {
    const root = window.document.documentElement;
    
    // Remove any existing theme classes
    root.classList.remove('light', 'dark', 'premium', 'standard');

    // Determine the actual theme based on the setting
    let actualTheme: 'light' | 'dark';
    
    if (theme === 'system') {
      actualTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    } else {
      actualTheme = theme;
    }

    // Add the appropriate classes
    root.classList.add(actualTheme, themeVariant);
    
    // Store the theme preference
    localStorage.setItem('theme', theme);
  }, [theme, themeVariant]);

  // Listen for system theme changes if using system preference
  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = () => {
      const root = window.document.documentElement;
      root.classList.remove('light', 'dark');
      root.classList.add(mediaQuery.matches ? 'dark' : 'light', themeVariant);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, themeVariant]);

  // Toggle between light and dark mode
  const toggleDarkMode = () => {
    setTheme(prevTheme => {
      if (prevTheme === 'light') return 'dark';
      if (prevTheme === 'dark') return 'light';
      
      // If system, toggle based on current system preference
      const systemIsDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      return systemIsDark ? 'light' : 'dark';
    });
  };

  return (
    <ThemeContext.Provider value={{ theme, themeVariant, setTheme, toggleDarkMode, isPremium }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
